import { useLocation } from "@tanstack/react-router";
import { Frown, Lightbulb } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Dropzone,
  DropzoneDescription,
  DropzoneGroup,
  DropzoneInput,
  DropzoneTitle,
  DropzoneUploadIcon,
  DropzoneZone,
} from "@/components/ui/dropzone";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { api } from "@/lib/api";

type FeedbackType = "idea" | "bug";

const MAX_FILE_BYTES = 10 * 1024 * 1024;

export function FeedbackDialog(props: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const location = useLocation();
  const [type, setType] = useState<FeedbackType>("idea");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [image, setImage] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);

  function reset() {
    setType("idea");
    setTitle("");
    setDescription("");
    setImage(null);
    setSubmitting(false);
  }

  function handleOpenChange(open: boolean) {
    if (!open) reset();
    props.onOpenChange(open);
  }

  const canSubmit = type && title.trim().length > 0 && description.trim().length > 0 && !submitting;

  async function handleSubmit() {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      // submitted_by / submitted_by_name are no longer sent: the command takes
      // them from the authenticated session, so feedback cannot be filed under
      // someone else's name.
      const formData = new FormData();
      formData.append("type", type);
      formData.append("title", title.trim());
      formData.append("description", description.trim());
      if (image) formData.append("image", image);
      formData.append("current_url", location.pathname);
      formData.append("user_agent", navigator.userAgent);

      await api("/api/commands/user-feedback/create", { method: "POST", formData });

      toast.success("Takk for tilbakemeldingen!");
      reset();
      props.onOpenChange(false);
    } catch (err) {
      console.error("Failed to submit feedback:", err);
      toast.error("Kunne ikke sende tilbakemelding. Prøv igjen.");
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={props.open} onOpenChange={handleOpenChange}>
      <DialogContent id="feedback-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle id="feedback-dialog-title">Send inn tilbakemelding</DialogTitle>
        </DialogHeader>

        <ToggleGroup
          type="single"
          value={type}
          onValueChange={(v) => v && setType(v as FeedbackType)}
          className="grid grid-cols-2 gap-2"
        >
          <ToggleGroupItem
            id="feedback-type-idea"
            value="idea"
            className="flex h-auto flex-col items-center gap-1 rounded-md border border-input bg-transparent p-3 data-[state=on]:border-accent-foreground/40 data-[state=on]:bg-accent"
          >
            <Lightbulb className="h-5 w-5" />
            <span className="text-sm">Jeg har en idé</span>
          </ToggleGroupItem>
          <ToggleGroupItem
            id="feedback-type-bug"
            value="bug"
            className="flex h-auto flex-col items-center gap-1 rounded-md border border-input bg-transparent p-3 data-[state=on]:border-accent-foreground/40 data-[state=on]:bg-accent"
          >
            <Frown className="h-5 w-5" />
            <span className="text-sm">Noe fungerer ikke</span>
          </ToggleGroupItem>
        </ToggleGroup>

        <div className="grid gap-2">
          <Label htmlFor="feedback-title">Tittel*</Label>
          <Input
            id="feedback-title"
            placeholder="Legg til kort overskrift"
            maxLength={200}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>

        <div className="grid gap-2">
          <Label htmlFor="feedback-description">Beskrivelse*</Label>
          <Textarea
            id="feedback-description"
            placeholder="Legg til detaljert beskrivelse"
            rows={4}
            maxLength={5000}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <div className="grid gap-2">
          <Label>Bilde (valgfritt)</Label>
          <Dropzone
            accept={{
              "image/*": [".jpg", ".jpeg", ".png", ".webp", ".gif"],
              "video/*": [".mp4", ".webm"],
            }}
            maxSize={MAX_FILE_BYTES}
            multiple={false}
            onDropAccepted={(files) => setImage(files[0] ?? null)}
            onDropRejected={() => toast.error("Filen er for stor eller har ugyldig format (maks 10MB).")}
          >
            <DropzoneZone className="p-4">
              <DropzoneInput />
              <DropzoneGroup className="gap-1">
                <DropzoneUploadIcon className="size-6 text-muted-foreground" />
                <DropzoneTitle className="text-sm">{image ? image.name : "Klikk for å laste opp"}</DropzoneTitle>
                <DropzoneDescription className="text-xs">Legg til bilder eller video (maks 10MB)</DropzoneDescription>
              </DropzoneGroup>
            </DropzoneZone>
          </Dropzone>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button
            id="feedback-cancel-btn"
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={submitting}
          >
            Avbryt
          </Button>
          <Button id="feedback-submit-btn" onClick={handleSubmit} disabled={!canSubmit}>
            Send inn
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
